import { Entypo } from '@expo/vector-icons';
import { useResponsiveFontSize } from '@/components/Util/reactResponsiveDimensions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import { AudioPlayer } from '../AudioPlayer';
import { Body } from './body';
import { styles } from './styles';
import { PressableNeolexon } from '@/components/PressableNeolexon';

const DEFAULT_STATE = {
  questionIndex: 0,
  showNextQuestion: true,
  showSolution: false,
};

function shuffle(a) {
  let j, x, i;
  for (i = a.length; i; i--) {
    j = Math.floor(Math.random() * i);
    x = a[i - 1];
    a[i - 1] = a[j];
    a[j] = x;
  }
}

export const HearingExerciseText = ({
  id,
  exercise,
  settings,
  currentExercise,
  exerciseState,
  audioUrl,
  showQuestions,
  back,
  skip,
  checkProgress,
}) => {
  const [state, setState] = useState(DEFAULT_STATE);
  const { questions, type } = exercise;
  const { setting, value, playbackRate } = settings;
  const modality = type === 'Sprechen' ? 'speak' : 'not-speak';
  const showBack = exerciseState === 'questions';
  const responsiveIconSize = useResponsiveFontSize(4);
  const [audioAutoPlay, setAudioAutoPlay] = useState(true);
  const [choices, setChoices] = useState([]);

  const backToAudio = useCallback(() => {
    back();
    setAudioAutoPlay(false);
  }, []);

  const onPressChoice = (key) => {
    const flagTheClickedChoice = choices.map((obj) => {
      if (obj.choiceAudio === key) {
        obj.isPressed = true;
      }
      return obj;
    });
    setChoices(flagTheClickedChoice);
  };

  const updateChoices = (index) => {
    if (settings.setting === 'closed') {
      setChoices(() => {
        let arr = [];
        for (let i = 0; i < settings.value; i++) {
          const data = neededQuestions?.[index].choices[i];
          data['isPressed'] = false;
          arr.push(data);
        }
        return arr;
      });
    }
  };

  const neededQuestions = useMemo(() => {
    let returnValue;
    if (modality !== 'speak') {
      returnValue = questions.filter(function (question) {
        return question.questionType === setting;
      });
    } else {
      if (setting === 'free') {
        returnValue = [
          {
            questionText: 'Erzählen Sie die Geschichte nach.',
            questionAudio: '/audio/erzaehlen_sie_nach.mp3',
          },
        ];
      } else {
        returnValue = questions.filter(function (question) {
          return question.questionText !== 'Erzählen Sie die Geschichte nach.';
        });
      }
    }
    // shuffle order of choices and reduce number of choices according to setting
    if (setting === 'closed') {
      returnValue.forEach(function (question) {
        shuffle(question.choices);
        // calculate how many choices need to be removed to match setting
        let elementsToRemove = question.choices.length - value;
        question.choices = question.choices.filter(function (choice) {
          if (choice.isCorrect === true || elementsToRemove < 1) {
            return true;
          } else {
            elementsToRemove--;
            return false;
          }
        });
        shuffle(question.choices);
      });
    }
    // shuffle order of questions
    shuffle(returnValue);
    return returnValue;
  }, [questions.toString(), setting, value]);

  const maxNumberOfQuestionIndex = neededQuestions?.length - 1;
  const maxIterations = state.questionIndex === maxNumberOfQuestionIndex;

  const onWeiterClick = () => {
    if (showBack) {
      if (maxIterations) {
        return skip();
      }
      checkProgress(false, true, true);
      setState((prevState) => {
        const newQuestionIndex = prevState.questionIndex + 1;
        updateChoices(newQuestionIndex);
        return {
          ...prevState,
          questionIndex: newQuestionIndex,
        };
      });
    }
    showQuestions();
  };

  const responsiveStyles = StyleSheet.create({
    responsiveText: {
      fontSize: responsiveIconSize,
      color: colors.primary,
      fontWeight: '700',
    },
  });

  useEffect(() => {
    setState(DEFAULT_STATE);
    updateChoices(0);
    setAudioAutoPlay(true);
  }, [currentExercise]);

  return (
    <View style={[styles.container, styles.flexContainer]}>
      <View style={styles.textBodyContainer}>
        {showBack ? (
          <Body
            id={id}
            questions={neededQuestions}
            questionIndex={state.questionIndex}
            settings={settings.setting}
            choices={choices}
            onPressChoice={onPressChoice}
            playbackRate={playbackRate}
          />
        ) : (
          <AudioPlayer
            isTracked
            uniqueKey={exercise.uniqueExercisePrepareDate ?? null}
            source={audioUrl}
            autoPlay={audioAutoPlay}
            playbackRate={playbackRate}
          />
        )}
      </View>
      <View style={[styles.row, styles.buttonsContainer]}>
        {showBack ? (
          <PressableNeolexon
            onPress={backToAudio}
            style={styles.weiterContainer}
          >
            <Entypo
              name="text-document-inverted"
              size={responsiveIconSize}
              color={colors.darkGrey}
            />
          </PressableNeolexon>
        ) : (
          <View />
        )}
        <PressableNeolexon
          onPress={onWeiterClick}
          style={[styles.row, styles.weiterContainer]}
        >
          <Text style={responsiveStyles.responsiveText}>weiter</Text>
          <Entypo
            name="arrow-bold-right"
            size={responsiveIconSize}
            color={colors.primary}
          />
        </PressableNeolexon>
      </View>
    </View>
  );
};
